import {
	createFileRoute,
	Link,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import { useEffect, useState, useTransition } from "react";
import {
	graphql,
	useLazyLoadQuery,
	useMutation,
	useRefetchableFragment,
} from "react-relay";
import { toast } from "sonner";
import { Icon } from "@/components/icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { type FiltersState, MemberFilterContent } from "@/ui/member-filter";
import { MemberFilters } from "@/utils/member-filters";
import type { tagsIdFiltros_preview$key } from "./__generated__/tagsIdFiltros_preview.graphql";
import type { tagsIdFiltrosPreviewQuery } from "./__generated__/tagsIdFiltrosPreviewQuery.graphql";
import type { tagsIdFiltrosQuery } from "./__generated__/tagsIdFiltrosQuery.graphql";
import type { tagsIdFiltrosUpdateMutation } from "./__generated__/tagsIdFiltrosUpdateMutation.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(standalone)/tags/$id/filtros",
)({
	component: RouteComponent,
});

const EMPTY_FILTERS: FiltersState = {
	any: [{ all: [{ fact: "", operator: "", value: "" }] }],
};

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { id: tagId } = Route.useParams();

	const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
	const debouncedFilters = useDebounce(filters, 800);
	const [isPending, startTransition] = useTransition();

	const data = useLazyLoadQuery<tagsIdFiltrosQuery>(
		graphql`
			query tagsIdFiltrosQuery($club: String!, $tagId: ID!) {
				club(slug: $club) {
					id
					# Filter options
					courses(first: 10000) {
						edges {
							node {
								id
								rawID
								name
							}
						}
					}
					offers(first: 10000) {
						edges {
							node {
								id
								rawID
								productName
							}
						}
					}
					badges(first: 10000) {
						edges {
							node {
								id
								rawID
								...memberBadgeFragment
							}
						}
					}
					groups {
						id
						spaces {
							id
							rawID
							name
							icon
						}
					}
					tags(first: 10000) {
						edges {
							node {
								id
								rawID
								text
							}
						}
					}

					...tagsIdFiltros_preview @arguments(filters: null)
				}
				node(id: $tagId) {
					... on Tag {
						id
						text
						filters
					}
				}
			}
		`,
		{ club: club.slug, tagId },
	);

	// Initialize filters from tag data
	useEffect(() => {
		if (data.node?.filters) {
			try {
				const parsedFilters = data.node.filters as FiltersState;
				if (parsedFilters?.any?.length > 0) {
					setFilters(parsedFilters);
				}
			} catch {
				// Keep default empty filters
			}
		}
	}, [data.node?.filters]);

	const [previewData, refetchPreview] = useRefetchableFragment<
		tagsIdFiltrosPreviewQuery,
		tagsIdFiltros_preview$key
	>(
		graphql`
			fragment tagsIdFiltros_preview on Club
				@refetchable(queryName: "tagsIdFiltrosPreviewQuery")
				@argumentDefinitions(filters: { type: "JSON" }) {
				clubMembers(first: 50, filters: $filters) {
					edges {
						node {
							id
							user {
								id
								displayName
								email
								avatarFallback
								photo {
									signedUrl
								}
							}
						}
					}
					pageInfo {
						totalCount
					}
				}
			}
		`,
		data.club,
	);

	// Refetch preview when filters change (debounced)
	useEffect(() => {
		const validFilters = getValidFilters(debouncedFilters);
		startTransition(() => {
			refetchPreview(
				{ filters: validFilters },
				{ fetchPolicy: "store-and-network" },
			);
		});
	}, [debouncedFilters, refetchPreview]);

	const [updateTagFilters, isUpdating] =
		useMutation<tagsIdFiltrosUpdateMutation>(graphql`
			mutation tagsIdFiltrosUpdateMutation($payload: UpdateTagInput!) {
				updateTag(updateTagInput: $payload) {
					id
					filters
					memberCount
				}
			}
		`);

	// Prepare filter options
	const courses =
		data.club.courses?.edges?.map((edge) => ({
			rawID: edge.node.rawID,
			name: edge.node.name,
		})) || [];

	const externalProducts =
		data.club.offers?.edges?.map((edge) => ({
			rawID: edge.node.rawID,
			productName: edge.node.productName ?? "",
		})) || [];

	const badges =
		data.club.badges?.edges?.map((edge) => ({
			rawID: edge.node.rawID,
			badge: edge.node,
		})) || [];

	const spaces =
		data.club.groups?.filter(Boolean).flatMap((group) =>
			group.spaces.filter(Boolean).map((space) => ({
				rawID: space.rawID,
				name: space.name,
				icon: space.icon ?? undefined,
			})),
		) || [];

	const tags =
		data.club.tags?.edges?.map((edge) => ({
			rawID: edge.node.rawID,
			text: edge.node.text,
		})) || [];

	const filterCategories = MemberFilters({
		courses,
		externalProducts,
		badges,
		spaces,
		tags,
	});

	const handleSave = () => {
		const validFilters = getValidFilters(filters);

		updateTagFilters({
			variables: {
				payload: {
					id: data.node?.id ?? "",
					filters: validFilters,
				},
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Filtros salvos com sucesso!");
				navigate({
					to: "/c/$club/audiencia/tags",
					params: { club: club.slug },
				});
			},
			onError: () => {
				toast.error("Erro ao salvar filtros. Tente novamente.");
			},
		});
	};

	const members = previewData?.clubMembers?.edges.map((e) => e.node) ?? [];
	const totalCount = previewData?.clubMembers?.pageInfo.totalCount ?? 0;

	return (
		<div className="flex flex-col h-screen bg-background">
			{/* Header */}
			<header className="sticky top-0 z-10 flex items-center justify-between h-14 px-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
				<div className="flex items-center gap-2 text-sm">
					<Link
						to="/c/$club/audiencia/tags"
						params={{ club: club.slug }}
						className="text-muted-foreground hover:text-foreground transition-colors"
					>
						Tags
					</Link>
					<span className="text-muted-foreground">/</span>
					<span className="font-medium">{data.node?.text}</span>
					<span className="text-muted-foreground">/</span>
					<span className="font-medium">Filtros</span>
				</div>

				<div className="flex items-center gap-2">
					<Button type="button" variant="ghost" size="sm" asChild>
						<Link to="/c/$club/audiencia/tags" params={{ club: club.slug }}>
							Cancelar
						</Link>
					</Button>
					<Button size="sm" disabled={isUpdating} onClick={handleSave}>
						{isUpdating ? (
							<>
								<Icon name="LoaderCircle" className="size-4 animate-spin" />
								Salvando...
							</>
						) : (
							<>
								<Icon name="Check" className="size-4" />
								Salvar
							</>
						)}
					</Button>
				</div>
			</header>

			{/* Main Content */}
			<div className="flex-1 flex overflow-hidden">
				{/* Left Sidebar - Filters */}
				<div className="w-80 border-r flex flex-col bg-muted/30">
					<div className="p-4 border-b">
						<h2 className="font-semibold">Filtros</h2>
						<p className="text-sm text-muted-foreground mt-1">
							Defina os critérios para atribuição
						</p>
					</div>
					<div className="flex-1 overflow-y-auto p-4">
						<MemberFilterContent
							filterCategories={filterCategories}
							filters={filters}
							onFiltersChange={setFilters}
						/>
					</div>
				</div>

				{/* Right Content - Preview */}
				<div className="flex-1 flex flex-col overflow-hidden">
					<div className="p-4 border-b">
						<div className="flex items-center gap-2">
							<h2 className="font-semibold">Pré-visualização</h2>
							{data.node?.text && (
								<span className="text-sm text-muted-foreground">
									— {data.node.text}
								</span>
							)}
						</div>
						<p className="text-sm text-muted-foreground mt-1">
							{totalCount}{" "}
							{totalCount === 1 ? "membro corresponde" : "membros correspondem"}{" "}
							aos filtros
						</p>
					</div>
					<div className="flex-1 overflow-auto p-4">
						<div className="rounded-md border">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Membro</TableHead>
										<TableHead>Email</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{isPending ? (
										Array.from({ length: 5 }).map((_, i) => (
											<TableRow key={i}>
												<TableCell>
													<div className="flex items-center gap-3">
														<Skeleton className="size-8 rounded-full" />
														<Skeleton className="h-4 w-32" />
													</div>
												</TableCell>
												<TableCell>
													<Skeleton className="h-4 w-48" />
												</TableCell>
											</TableRow>
										))
									) : members.length === 0 ? (
										<TableRow>
											<TableCell
												colSpan={2}
												className="h-32 text-center text-muted-foreground"
											>
												Nenhum membro corresponde aos filtros selecionados
											</TableCell>
										</TableRow>
									) : (
										members.map((member) => (
											<TableRow key={member.id}>
												<TableCell>
													<div className="flex items-center gap-3">
														<Avatar className="size-8">
															<AvatarImage
																src={member.user.photo?.signedUrl ?? undefined}
															/>
															<AvatarFallback className="text-xs">
																{member.user.avatarFallback}
															</AvatarFallback>
														</Avatar>
														<span className="font-medium">
															{member.user.displayName}
														</span>
													</div>
												</TableCell>
												<TableCell className="text-muted-foreground">
													{member.user.email}
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</div>
						{totalCount > 50 && (
							<p className="text-sm text-muted-foreground text-center mt-4">
								Mostrando 50 de {totalCount} membros
							</p>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

function getValidFilters(filters: FiltersState): FiltersState | null {
	const validFilters: FiltersState = {
		any: filters.any
			.map((group) => ({
				all: group.all.filter(
					(rule) => rule.fact && rule.operator && rule.value,
				),
			}))
			.filter((group) => group.all.length > 0),
	};

	if (validFilters.any.length === 0) {
		return null;
	}

	return validFilters;
}
